import React, { useState, useRef } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  PlayArrow as PlayIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  DataObject as DataIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import config from '../../config/environment'

interface QueryResult {
  columns: string[]
  rows: any[]
  affectedRows?: number
  lastInsertId?: number
  executionTime: number
}

interface QueryRunnerProps {
  onError: (error: string | null) => void
}

/**
 * SQL Query Runner Component
 * Advanced interface for executing custom SQL queries
 */
const QueryRunner: React.FC<QueryRunnerProps> = ({ onError }) => {
  const theme = useTheme()
  const textareaRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [isExecuting, setIsExecuting] = useState(false)
  const [result, setResult] = useState<QueryResult | null>(null)
  const [queryHistory, setQueryHistory] = useState<Array<{
    query: string
    timestamp: string
    success: boolean
    executionTime?: number
  }>>([])

  // Get API base URL
  const getApiBaseUrl = () => {
    return config.API_BASE_URL
  }

  // Sample queries for quick testing
  const sampleQueries = [
    {
      name: 'Show all tables',
      query: "SELECT name, type FROM sqlite_master WHERE type IN ('table', 'view') ORDER BY name;"
    },
    {
      name: 'Database info',
      query: "SELECT sqlite_version() as sqlite_version;"
    },
    {
      name: 'Table row counts',
      query: `SELECT 
  name as table_name,
  (SELECT COUNT(*) FROM sqlite_master WHERE type='table' AND name=m.name) as is_table
FROM sqlite_master m 
WHERE type='table' 
ORDER BY name;`
    }
  ]

  const executeQuery = async () => {
    if (!query.trim()) {
      onError('Please enter a SQL query')
      return
    }

    setIsExecuting(true)
    onError(null)
    
    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) {
        onError('Authentication token not found')
        return
      }

      const response = await fetch(`${getApiBaseUrl()}/api/db-admin/query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sql: query })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Query execution failed')
      }

      setResult(data.data)
      
      // Add to history
      const historyItem = {
        query: query.trim(),
        timestamp: new Date().toLocaleString(),
        success: true,
        executionTime: data.data.executionTime
      }
      setQueryHistory(prev => [historyItem, ...prev.slice(0, 9)]) // Keep last 10 queries

    } catch (error) {
      console.error('Query execution error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Query execution failed'
      onError(errorMessage)
      
      // Add failed query to history
      const historyItem = {
        query: query.trim(),
        timestamp: new Date().toLocaleString(),
        success: false
      }
      setQueryHistory(prev => [historyItem, ...prev.slice(0, 9)])
    } finally {
      setIsExecuting(false)
    }
  }

  const clearQuery = () => {
    setQuery('')
    setResult(null)
    onError(null)
  }

  const loadSampleQuery = (sampleQuery: string) => {
    setQuery(sampleQuery)
    setResult(null)
    onError(null)
  }

  const exportResults = (format: 'json' | 'csv') => {
    if (!result || result.rows.length === 0) return

    let content: string
    let mimeType: string
    let filename: string

    if (format === 'json') {
      content = JSON.stringify(result.rows, null, 2)
      mimeType = 'application/json'
      filename = 'query_results.json'
    } else {
      // CSV export
      const headers = result.columns.join(',')
      const csvRows = result.rows.map(row => 
        result.columns.map(col => {
          const value = row[col]
          const stringValue = value === null ? '' : String(value)
          return stringValue.includes(',') || stringValue.includes('"') 
            ? `"${stringValue.replace(/"/g, '""')}"` 
            : stringValue
        }).join(',')
      )
      content = [headers, ...csvRows].join('\n')
      mimeType = 'text/csv'
      filename = 'query_results.csv'
    }

    const blob = new Blob([content], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.ctrlKey && event.key === 'Enter') {
      event.preventDefault()
      executeQuery()
    }
  }

  const isSelectQuery = query.trim().toLowerCase().startsWith('select') || 
                        query.trim().toLowerCase().startsWith('with')

  return (
    <Grid container spacing={3}>
      {/* Query Input */}
      <Grid item xs={12} lg={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" component="h3">
                SQL Query Runner
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Clear query (Ctrl+C)">
                  <IconButton onClick={clearQuery} disabled={isExecuting}>
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <TextField
              ref={textareaRef}
              fullWidth
              multiline
              rows={8}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your SQL query here...&#10;&#10;Examples:&#10;SELECT * FROM table_name LIMIT 10;&#10;PRAGMA table_info(table_name);&#10;&#10;Press Ctrl+Enter to execute"
              variant="outlined"
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  fontSize: '0.875rem'
                }
              }}
            />

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={isExecuting ? <CircularProgress size={16} /> : <PlayIcon />}
                onClick={executeQuery}
                disabled={isExecuting || !query.trim()}
                sx={{ minWidth: 120 }}
              >
                {isExecuting ? 'Executing...' : 'Execute'}
              </Button>

              {query.trim() && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    size="small" 
                    label={isSelectQuery ? 'SELECT Query' : 'Modification Query'}
                    color={isSelectQuery ? 'primary' : 'warning'}
                    variant="outlined"
                  />
                  {!isSelectQuery && (
                    <Chip
                      size="small"
                      icon={<WarningIcon />}
                      label="Will modify data"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Tip: Press Ctrl+Enter to execute the query quickly
            </Typography>
          </CardContent>
        </Card>

        {/* Query Results */}
        {result && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" component="h3">
                  Query Results
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {result.executionTime}ms
                    </Typography>
                  </Box>
                  {result.rows.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportResults('json')}
                      >
                        JSON
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportResults('csv')}
                      >
                        CSV
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>

              {result.columns && result.columns.length > 0 ? (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2">
                        Query executed successfully • {result.rows.length} rows returned
                      </Typography>
                      {result.affectedRows !== undefined && (
                        <Chip size="small" label={`${result.affectedRows} rows affected`} />
                      )}
                      {result.lastInsertId && (
                        <Chip size="small" label={`Last insert ID: ${result.lastInsertId}`} />
                      )}
                    </Box>
                  </Alert>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {result.columns.map((column, index) => (
                            <TableCell key={index}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {column}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.rows.map((row, rowIndex) => (
                          <TableRow key={rowIndex} hover>
                            {result.columns.map((column, colIndex) => (
                              <TableCell key={colIndex}>
                                {row[column] === null ? (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    NULL
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {String(row[column])}
                                  </Typography>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      Query executed successfully
                    </Typography>
                    {result.affectedRows !== undefined && (
                      <Chip size="small" label={`${result.affectedRows} rows affected`} />
                    )}
                    {result.lastInsertId && (
                      <Chip size="small" label={`Last insert ID: ${result.lastInsertId}`} />
                    )}
                  </Box>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Grid>

      {/* Sidebar */}
      <Grid item xs={12} lg={4}>
        {/* Sample Queries */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              Sample Queries
            </Typography>
            <List sx={{ py: 0 }}>
              {sampleQueries.map((sample, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => loadSampleQuery(sample.query)}
                  sx={{ px: 0, py: 1 }}
                >
                  <ListItemText
                    primary={sample.name}
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{ 
                          fontFamily: 'monospace',
                          display: 'block',
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {sample.query}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Query History */}
        {queryHistory.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Query History
              </Typography>
              <List sx={{ py: 0 }}>
                {queryHistory.map((item, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => loadSampleQuery(item.query)}
                    sx={{ px: 0, py: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" noWrap>
                            {item.query.substring(0, 30)}...
                          </Typography>
                          <Chip
                            size="small"
                            label={item.success ? 'Success' : 'Failed'}
                            color={item.success ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {item.timestamp}
                          </Typography>
                          {item.executionTime && (
                            <Typography variant="caption" color="text.secondary">
                              • {item.executionTime}ms
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  )
}

export default QueryRunner